import { Component, OnDestroy } from '@angular/core';
import { SignatureType } from '@completion/enums';
import { SignatureMatrixTemplate, SignatureMatrixTemplates } from '@completion/models';
import { getCompanyMatrixTypes, getCSMatrixTypes, getCurrentProject, getDefaultSignatureMatrices } from '@completion/selectors';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { CompanySignatureTypeLoad, GetCheckSheetMatrixTypes } from '@completion/actions';

@Component({
  selector: 'app-signature-matrix-template',
  templateUrl: './signature-matrix-template.component.html',
  styleUrls: ['./signature-matrix-template.component.scss'],
  standalone: false,
})
export class SignatureMatrixTemplateComponent implements OnDestroy {
  checkSheetMatrixTypes: string[] = [];
  companyMatrixTypes: string[] = [];
  signatureMatrixTemplates: SignatureMatrixTemplates = null;
  destroy$: Subject<void> = new Subject();
  constructor(private store: Store) {
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project) => {
        if (project) {
          this.store.dispatch(new CompanySignatureTypeLoad());
          this.store.dispatch(new GetCheckSheetMatrixTypes(project.id));
        }
      });

    this.store
      .select(getCompanyMatrixTypes)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyMatrixTypes) => {
        if (companyMatrixTypes && companyMatrixTypes.length > 0) {
          this.companyMatrixTypes = companyMatrixTypes;
        }
      });
    this.store
      .select(getDefaultSignatureMatrices)
      .pipe(takeUntil(this.destroy$))
      .subscribe((signatureMatrixTemplates: SignatureMatrixTemplates) => {
        this.signatureMatrixTemplates = signatureMatrixTemplates;
      });

    this.store
      .select(getCSMatrixTypes)
      .pipe(takeUntil(this.destroy$))
      .subscribe((checkSheetMatrixTypes) => {
        if (checkSheetMatrixTypes && checkSheetMatrixTypes.length > 0) {
          this.checkSheetMatrixTypes = checkSheetMatrixTypes;
        }
      });
  }
  getSignatureTypes() {
    const signatureTypes = Object.keys(SignatureType);
    return signatureTypes.slice(0, -2);
  }

  getCompanySignatureTypes(): string[] {
    return this.companyMatrixTypes;
  }
  getCheckSheetSignatureTypes(): string[] {
    return this.checkSheetMatrixTypes;
  }

  convertSignatureTypeToName(property: string): string {
    const type = SignatureType[property];
    switch (type) {
      case SignatureType.CSA_CP:
        return 'CP Checksheet';
      case SignatureType.CSA_MCP:
        return 'MCP Checksheet';
      case SignatureType.COMPANY:
        return 'Company Checksheet';
      case SignatureType.CHECK_SHEET:
        return 'By Checksheet';
      case SignatureType.MCC:
        return 'Mc Certificate';
      case SignatureType.RFCC:
        return 'Rfc Certificate';
      case SignatureType.RFOC:
        return 'Rfo Certificate';
      case SignatureType.HANDOVER:
        return 'Handover';
      default:
        return property;
    }
  }

  getTypeSummary(property: string): string {
    const type = SignatureType[property];
    const templates = this.getTemplatesForType(type);
    if (!templates || templates.length === 0) return 'No templates configured';
    const disciplines = new Set(templates.filter((t) => t.discipline).map((t) => t.discipline.disciplineNumber));
    return `${templates.length} row${templates.length !== 1 ? 's' : ''}${disciplines.size > 0 ? ', ' + disciplines.size + ' discipline' + (disciplines.size !== 1 ? 's' : '') : ''}`;
  }

  getCustomTypeSummary(signatureType: SignatureType, customType: string): string {
    const templates = this.getTemplatesForType(signatureType, customType);
    if (!templates || templates.length === 0) return 'No templates configured';
    return `${templates.length} row${templates.length !== 1 ? 's' : ''}`;
  }

  private getTemplatesForType(type: SignatureType, customType?: string): SignatureMatrixTemplate[] {
    if (!this.signatureMatrixTemplates || !this.signatureMatrixTemplates[type]) return [];
    const templates = this.signatureMatrixTemplates[type];
    if (customType) {
      return templates.filter((t) => t.projectDefinedType === customType);
    }
    return templates;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected readonly SignatureType = SignatureType;
}
